/**
 * The brand-input CATALOGUE — derived from `METRIC_REGISTRY`, never hand-listed
 * (VRG-459).
 *
 * Three surfaces collect the same numbers: the in-card popover on a `needs_input`
 * metric, `Settings -> Analytics inputs`, and (later) the marketing site's signup
 * form. Hand-writing the field list on any one of them means the day someone adds
 * `inputs: [Input.GrossMarginRate]` to a metric, two of the three silently stop
 * asking for what the metric now needs — and the metric renders an em-dash with no
 * route to fixing it. So the catalogue is a FUNCTION of the registry, and adding an
 * input to a metric adds the field everywhere by construction.
 *
 * KEYED BY INPUT, NOT BY METRIC, and that is the whole point of this file. One
 * input feeds several metrics — a gross-margin rate is wanted by every metric that
 * converts units into profit — so a per-metric form would ask for the same number
 * repeatedly and let a brand give two different answers to one question. Entering
 * it once must light every metric that was waiting on it.
 *
 * ⚠️ SATISFIABILITY IS NOT "APPEARS IN THE REGISTRY". An input declared only by
 * metrics that are `blocked` can never unblock anything — `metricTier()` puts
 * `blocked` ahead of `inputs` precisely because supplying an assumption does not
 * help when there is nothing to apply it to. Collecting such a value would be
 * asking a brand for a number in exchange for nothing, so `isSatisfiableMetricInput`
 * excludes it and the write endpoint rejects it rather than storing it silently.
 */
import {IAnalyticsMetric} from './analytics-metric.interface.js';
import {METRIC_REGISTRY} from './metric-registry.js';
import {
  METRIC_INPUT_LABELS,
  MetricInputKey,
  MetricInputProvenance,
  MetricInputUnit,
} from './metric-taxonomy.js';

/**
 * The shape and bounds of one input's value.
 *
 * ⚠️ THE UNIT IS THE DANGEROUS FIELD, and no validator here can protect it. A
 * units error is a VALID number: `250000` entered as dollars where cents were
 * expected passes every bound below and multiplies the headline ROI by 100, and
 * nothing distinguishes it from a genuinely large company. The defence is on the
 * surface, not in this file — state the unit inline in the field, echo the parsed
 * value as it is typed, and echo the CONSEQUENCE (the resulting metric value and
 * the new multiple) before the save commits. A wrong magnitude becomes an absurd
 * output, which a person catches instantly and a range check never will.
 */
export interface IMetricInputSpec {
  key: MetricInputKey;
  /** Matrix/field label. Short by rule — see working-conventions §25. */
  label: string;
  /** One sentence, customer-facing, stating exactly what is being asked for. */
  description: string;
  unit: MetricInputUnit;
  /** Inclusive lower bound. */
  min: number;
  /**
   * Inclusive upper bound, or null where none is defensible. A rate is bounded by
   * its own definition; a currency amount is not, and inventing a ceiling would
   * reject the largest customers we have.
   */
  max: number | null;
  /** True when a fractional value is meaningless (minor currency units). */
  integral: boolean;
}

/**
 * Per-input shape. `rate` values are stored 0–1 (never 0–100) and `currency`
 * values in MINOR units, matching `IMetricInputValue`'s own contract — the
 * cents/dollars split is the 100x error this codebase has already paid for once
 * in the activation-pricing module, so the storage unit is stated in exactly one
 * place and every other layer reads it from here.
 */
export const METRIC_INPUT_SPECS: Record<MetricInputKey, IMetricInputSpec> = {
  [MetricInputKey.CounterfeitConversionRate]: {
    key: MetricInputKey.CounterfeitConversionRate,
    label: METRIC_INPUT_LABELS[MetricInputKey.CounterfeitConversionRate],
    description:
      'Share of failed verifications that represent a genuine counterfeit encounter rather than a mis-scan.',
    unit: 'rate',
    min: 0,
    max: 1,
    integral: false,
  },
  [MetricInputKey.CustomerAcquisitionCost]: {
    key: MetricInputKey.CustomerAcquisitionCost,
    label: METRIC_INPUT_LABELS[MetricInputKey.CustomerAcquisitionCost],
    description: 'What you pay, on average, to acquire one new customer.',
    unit: 'currency_minor',
    min: 0,
    max: null,
    integral: true,
  },
  [MetricInputKey.CustomerLifetimeValue]: {
    key: MetricInputKey.CustomerLifetimeValue,
    label: METRIC_INPUT_LABELS[MetricInputKey.CustomerLifetimeValue],
    description: 'Expected lifetime value of one customer.',
    unit: 'currency_minor',
    min: 0,
    max: null,
    integral: true,
  },
  [MetricInputKey.ReturnFraudAnnualLoss]: {
    key: MetricInputKey.ReturnFraudAnnualLoss,
    label: METRIC_INPUT_LABELS[MetricInputKey.ReturnFraudAnnualLoss],
    description:
      'Value of goods you lose each year to return fraud you do not currently catch.',
    unit: 'currency_minor',
    min: 0,
    max: null,
    integral: true,
  },
  [MetricInputKey.GrossMarginRate]: {
    key: MetricInputKey.GrossMarginRate,
    label: METRIC_INPUT_LABELS[MetricInputKey.GrossMarginRate],
    description: 'Gross margin, used to convert retained units into profit.',
    unit: 'rate',
    min: 0,
    max: 1,
    integral: false,
  },
};

/**
 * One field a brand can be asked for, plus the metrics it unblocks.
 *
 * `metric_ids` / `metric_titles` are what let a form say "Used by Return Fraud
 * Prevented, Scan Engagement Rate" instead of asking for a number with no stated
 * purpose. They are also the honest answer to "why do you want this?" — if the
 * list is empty the field should not be on screen at all, which is what
 * `metricInputCatalogue` guarantees by construction.
 */
export interface IMetricInputDefinition extends IMetricInputSpec {
  metric_ids: string[];
  metric_titles: string[];
}

/** Metrics an input can actually unblock: declared by it AND not blocked. */
export const metricsUnblockedByInput = (
  key: MetricInputKey,
  registry: IAnalyticsMetric[] = METRIC_REGISTRY,
): IAnalyticsMetric[] =>
  registry.filter(m => !m.blocked && m.inputs.includes(key));

/**
 * True when supplying this input can change something. False for an unknown key
 * and for a key only ever declared by blocked metrics.
 */
export const isSatisfiableMetricInput = (
  key: MetricInputKey,
  registry: IAnalyticsMetric[] = METRIC_REGISTRY,
): boolean =>
  Object.prototype.hasOwnProperty.call(METRIC_INPUT_SPECS, key) &&
  metricsUnblockedByInput(key, registry).length > 0;

/**
 * Every field worth collecting, in registry order of first appearance.
 *
 * Order is deliberate rather than alphabetical: a form grouped the way the
 * catalogue is defined puts the inputs for the headline value metrics first,
 * which is the order a brand meets them on the page.
 */
export const metricInputCatalogue = (
  registry: IAnalyticsMetric[] = METRIC_REGISTRY,
): IMetricInputDefinition[] => {
  const seen = new Set<MetricInputKey>();
  const ordered: MetricInputKey[] = [];

  for (const metric of registry) {
    if (metric.blocked) continue;
    for (const key of metric.inputs) {
      // An input declared by a metric but absent from METRIC_INPUT_SPECS has no
      // shape, no bounds and no label, so it cannot be rendered as a field. The
      // registry guard asserts this never happens; skipping rather than throwing
      // keeps a bad registry entry from taking the whole analytics page down.
      if (!Object.prototype.hasOwnProperty.call(METRIC_INPUT_SPECS, key))
        continue;
      if (seen.has(key)) continue;
      seen.add(key);
      ordered.push(key);
    }
  }

  return ordered.map(key => {
    const metrics = metricsUnblockedByInput(key, registry);
    return {
      ...METRIC_INPUT_SPECS[key],
      metric_ids: metrics.map(m => m.id),
      metric_titles: metrics.map(m => m.title),
    };
  });
};

/**
 * Type and range validation — table stakes, and deliberately described as such.
 *
 * Returns the customer-facing reason, or NULL when the value is acceptable.
 *
 * A `{ok: boolean}` discriminated union would read better at the call site and is
 * not used here for a concrete reason: `apps/api` compiles with
 * `strictNullChecks: false`, under which TypeScript does not narrow a union by a
 * boolean-literal discriminant, so `if (!result.ok) result.reason` fails to compile
 * there. `string | null` needs no narrowing and behaves identically under every
 * strictness setting in this monorepo.
 *
 * This catches a negative acquisition cost and a 350% margin. It does NOT catch the
 * failure that actually matters (see `IMetricInputSpec`), and no arithmetic check
 * can. Treat a null return as "not obviously junk", never as "correct".
 */
export const metricInputValueError = (
  key: MetricInputKey,
  value: number,
  registry: IAnalyticsMetric[] = METRIC_REGISTRY,
): string | null => {
  if (!isSatisfiableMetricInput(key, registry)) {
    return `'${key}' is not an input any available metric can use.`;
  }

  const spec = METRIC_INPUT_SPECS[key];

  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return `${spec.label} must be a finite number.`;
  }
  if (value < spec.min) {
    return `${spec.label} cannot be below ${spec.min}.`;
  }
  if (spec.max !== null && value > spec.max) {
    return spec.unit === 'rate'
      ? `${spec.label} is a rate between 0 and 1, not a percentage.`
      : `${spec.label} cannot be above ${spec.max}.`;
  }
  if (spec.integral && !Number.isInteger(value)) {
    return `${spec.label} is in minor units (e.g. cents) and must be a whole number.`;
  }

  return null;
};

/**
 * Provenance in the order a value hardens: a default, then a sales-form answer,
 * then a number someone chose in-app. Exported so a UI can sort or badge by it
 * without re-deriving the ranking.
 */
export const METRIC_INPUT_PROVENANCE_ORDER: MetricInputProvenance[] = [
  'vera_default',
  'signup',
  'user',
];
